import {
  Button,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Paper,
  FormControlLabel,
  Checkbox,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Grid,
} from '@mui/material';
import Page from '../../../../components/page/Page';
import useThemeMediaQuery from '../../../../../core/hooks/useThemeMediaQuery';
import { useArticlesCore } from '../../../../../core/modules/articles/index';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMeCore } from '../../../../../core/modules/me';
import { useNavigationCore } from '../../../../../core/router/navigation';
import { useCompanyCore } from '../../../../../core/modules/company';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { debounce, isEmpty } from 'lodash';
import Spinner from '../../../../components/Spinner';
import { useMasterCore } from '../../../../../core/modules/master';
import AddHeader from '../../../../components/administration/article/AddHeader';
import { ProductCategory } from '../../../../../core/interfaces/category.interface';
import { ExtraField } from '../../../../../core/interfaces/type.interface';
import { ERROR } from '../../../../../core/constants/errorCode';
import { PhotoCamera } from '@mui/icons-material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ClearIcon from '@mui/icons-material/Clear';
import CopyButton from '../../../../components/shared/CopyButton';
import { usePriceSuggestionCore, formatPrice } from '../../../../../core/modules/price-suggestion';
import { usePrintLabel } from '../../../../../core/hooks/usePrintLabel';
import { Article } from '../../../../../core/interfaces/article.interface';
import PrintIcon from '@mui/icons-material/Print';
import CategorySelect from '../../../../components/shared/CategorySelect';
import ReactBarcode from 'react-barcode';
import { useSearchParams } from 'react-router-dom';

interface FormData {
  name: string;
  code?: string;
  brand: string;
  alternative_code?: string;
  description?: string;
  notes?: string;
  pack?: boolean;
  price?: number | null;
  width_cm?: number | null;
  length_cm?: number | null;
  depth_cm?: number | null;
  weight_kg?: number | null;
  product_category_id?: number;
  product_type_id?: number;
  extra_fields?: Record<string, any>;
}

interface CreateArticleProps {
  embedded?: boolean;
  mode?: 'create' | 'edit';
  onCreated?: (article: Article) => void;
  onUpdated?: (article: Article) => void;
  onClose?: () => void;
  initialArticle?: Article;
}

const CreateArticle = ({ embedded, mode = 'create', onCreated, onUpdated, onClose, initialArticle }: CreateArticleProps) => {
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const { user } = useMeCore();
  const { companyId } = useCompanyCore();
  const { errorCode, article, postArticle, postArticleFile, putArticle } =
    useArticlesCore();
  const { goToWelcome, goToArticles } = useNavigationCore();
  const [searchParams] = useSearchParams();
  const isCopy = searchParams.get('copy') === 'true';
  const {
    productTypes,
    productCategories,
    genres,
    formats,
    getCategories,
    getTypes,
    getFormats,
    getGenres,
  } = useMasterCore();
  const { printArticle } = usePrintLabel();

  useEffect(() => {
    if (
      !user ||
      (!user.privileges.superAdmin && !user.privileges.writeProduct)
    ) {
      goToWelcome();
    }
  }, [user, goToWelcome]);

  const [loading, setLoading] = useState(false);
  const [withoutCode, setWithoutCode] = useState(false);
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [format, setFormat] = useState<number | undefined>();
  const [extraFieldValues, setExtraFieldValues] = useState<Record<string, any>>(
    {}
  );
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdArticle, setCreatedArticle] = useState<Article | null>(null);

  const [productDescription, setProductDescription] = useState('');
  const [prefix, setPrefix] = useState('SC');
  const [productType, setProductType] = useState<number>();
  const [volumetricWeightKg, setVolumetricWeightKg] = useState<number>(0);
  const [width, setWidth] = useState<number>(0);
  const [length, setLength] = useState<number>(0);
  const [depth, setDepth] = useState<number>(0);
  const [productCode, setProductCode] = useState<string>('');
  const [alternativeCode, setAlternativeCode] = useState<string>('');
  const [isPack, setIsPack] = useState(false);

  // Hook del módulo price-suggestion
  const {
    suggestedPrice,
    loading: loadingSuggestion,
    error: suggestionError,
    getSuggestedPrice: getSuggestedPriceCore,
    clearSuggestion,
  } = usePriceSuggestionCore();

  // Schema dinámico basado en si hay categorías disponibles para el tipo de producto seleccionado
  const schema = useMemo(() => {
    const hasCategoriesForType = productType ? productCategories.filter(
      (category) => (category as ProductCategory).product_type_id === productType
    ).length > 0 : false;

    const numericField = () =>
      yup
        .number()
        .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
        .typeError('Debe ingresar un número válido')
        .optional();

    return yup.object().shape({
      code: yup.string().optional(),
      alternative_code: yup.string().optional(),
      name: yup.string().required('El nombre del producto es obligatorio'),
      brand: yup.string().required('La marca es obligatoria'),
      notes: yup.string().optional(),
      price: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
        .typeError('Debe ingresar un número válido')
        .min(0, 'El precio no puede ser negativo')
        .nullable()
        .optional(),
      product_category_id: hasCategoriesForType
        ? yup.number().required('La categoría del producto es obligatoria')
        : yup.number().optional(),
      product_type_id: yup
        .number()
        .required('El tipo de producto es obligatorio'),
      format: numericField(),
      genre: numericField(),
      width_cm: numericField(),
      length_cm: numericField(),
      depth_cm: numericField(),
      weight_kg: numericField(),
      extra_fields: yup.object().optional(),
    });
  }, [productCategories, productType]);

  const { control, formState, handleSubmit, setValue, reset, trigger } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  const resetForm = useCallback(() => {
    // Limpiar todos los estados locales primero
    setWithoutCode(false);
    setIsPack(false);
    setFormat(undefined);
    setProductType(undefined);
    setExtraFieldValues({});
    setName('');
    setBrand('');
    setWidth(0);
    setLength(0);
    setDepth(0);
    setProductCode('');
    setAlternativeCode('');
    setProductDescription('');
    setVolumetricWeightKg(0);
    setPrefix('SC');
    setNewFiles([]);
    clearSuggestion();
    
    // Resetear el formulario completo de react-hook-form a valores vacíos
    reset({
      code: '',
      alternative_code: '',
      name: '',
      brand: '',
      notes: '',
      price: undefined as any,
      product_category_id: undefined as any,
      product_type_id: undefined as any,
      width_cm: undefined as any,
      length_cm: undefined as any,
      depth_cm: undefined as any,
      weight_kg: undefined as any,
      extra_fields: {},
      format: undefined as any,
      genre: undefined as any,
    });
  }, [reset, clearSuggestion]);

  const debouncedChangeHandler = useMemo(
    () =>
      debounce((value: string, field: string) => {
        setValue(field as any, value.toUpperCase());
      }, 1000),
    [setValue]
  );

  const onSubmit = useCallback(
    (values: FormData) => {
      if (!companyId) {
        return;
      }

      setLoading(true);

      const payload = {
        ...values,
        pack: isPack,
        code: withoutCode ? prefix : values.code,
        description: productDescription,
        volumetric_weight_kg: volumetricWeightKg,
        product_category_id: values.product_category_id || 0,
        extra_fields: (values.extra_fields || {}) as object,
        price: values.price ?? undefined,
        width_cm: values.width_cm ?? undefined,
        length_cm: values.length_cm ?? undefined,
        depth_cm: values.depth_cm ?? undefined,
        weight_kg: values.weight_kg ?? undefined,
      };

      const buildArticleData = (id: number, codeValue: string, base?: Article): Article => {
        const baseFiles = base?.files ?? [];
        return {
          id,
          name: values.name,
          code: withoutCode ? codeValue : values.code || base?.code || '',
          brand: values.brand,
          alternative_code: values.alternative_code,
          description: productDescription,
          notes: values.notes,
          pack: isPack,
          price: values.price ?? base?.price ?? 0,
          width_cm: values.width_cm ?? base?.width_cm ?? 0,
          length_cm: values.length_cm ?? base?.length_cm ?? 0,
          depth_cm: values.depth_cm ?? base?.depth_cm ?? 0,
          weight_kg: values.weight_kg ?? base?.weight_kg ?? 0,
          volumetric_weight_kg: volumetricWeightKg ?? base?.volumetric_weight_kg ?? 0,
          extra_fields: values.extra_fields || base?.extra_fields || {},
          product_category_id: values.product_category_id || base?.product_category_id || 0,
          product_id: values.product_type_id || base?.product_id || 0,
          created_at: base?.created_at ?? Math.floor(Date.now() / 1000),
          files: [
            ...baseFiles,
            ...newFiles.map((file, index) => ({ ID: index + baseFiles.length, URL: URL.createObjectURL(file) })),
          ],
        };
      };

      if (mode === 'edit' && initialArticle) {
        putArticle(
          companyId,
          initialArticle.id,
          payload,
          async (id: number) => {
            for (const file of newFiles) {
              await postArticleFile(companyId, id, file);
            }

            const updatedArticle = buildArticleData(id, initialArticle.code || '', initialArticle);

            if (embedded && onUpdated) {
              onUpdated(updatedArticle);
              setLoading(false);
              return;
            }

            setCreatedArticle(updatedArticle);
            setShowSuccessModal(true);
            setLoading(false);
          },
          () => {
            setLoading(false);
          }
        );
        return;
      }

      postArticle(
        companyId,
        payload,
        async (id: number, generatedCode: string) => {
          for (const file of newFiles) {
            await postArticleFile(companyId, id, file);
          }

          const articleData = buildArticleData(id, generatedCode);

          if (onCreated) {
            onCreated(articleData);
            setLoading(false);
            return;
          }
          setCreatedArticle(articleData);
          setShowSuccessModal(true);
          resetForm();
          setLoading(false);
        },
        () => {
          setLoading(false);
        }
      );
    },
    [
      companyId,
      mode,
      initialArticle,
      isPack,
      withoutCode,
      prefix,
      productDescription,
      volumetricWeightKg,
      embedded,
      onUpdated,
      postArticle,
      putArticle,
      postArticleFile,
      resetForm,
      newFiles,
      onCreated,
    ]
  );

  useEffect(() => {
    if (companyId) {
      getTypes(companyId, 'product');
      getCategories(companyId, 'product');
      getGenres(companyId);
      getFormats(companyId);
    }
  }, [companyId, getTypes, getCategories, getGenres, getFormats]);

  useEffect(() => {
    return () => {
      clearSuggestion();
    };
  }, [clearSuggestion]);

  // Resetear formulario al montar si es creación nueva (sin initialArticle y no es copia)
  useEffect(() => {
    if (mode === 'create' && !initialArticle && !isCopy) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar al montar

  useEffect(() => {
    // Solo cargar datos si hay initialArticle (para copiar) o si hay article del store Y es modo copia
    const src = initialArticle || (isCopy && article ? article : null);
    if (!src) {
      return; // No cargar nada si es creación nueva
    }

    setValue('code', src.code, { shouldValidate: true, shouldDirty: true });
    setValue('product_category_id', src.product_category_id, { shouldValidate: true, shouldDirty: true });
    setValue('name', src.name, { shouldValidate: true, shouldDirty: true });
    setValue('brand', src.brand, { shouldValidate: true, shouldDirty: true });
    setValue('alternative_code', src.alternative_code, { shouldValidate: true, shouldDirty: true });
    setValue('width_cm', src.width_cm, { shouldValidate: true, shouldDirty: true });
    setValue('length_cm', src.length_cm, { shouldValidate: true, shouldDirty: true });
    setValue('depth_cm', src.depth_cm, { shouldValidate: true, shouldDirty: true });
    setValue('weight_kg', src.weight_kg, { shouldValidate: true, shouldDirty: true });
    setValue('notes', src.notes, { shouldValidate: true, shouldDirty: true });
    setValue('price', src.price, { shouldValidate: true, shouldDirty: true });
    setValue('extra_fields', src.extra_fields || {}, { shouldValidate: true, shouldDirty: true });

    if (mode === 'create') {
      setWithoutCode(true);
    } else {
      setWithoutCode(!src.code);
    }
    setName(src.name);
    setBrand(src.brand);
    setWidth(src.width_cm);
    setLength(src.length_cm);
    setDepth(src.depth_cm);
    setVolumetricWeightKg(src.volumetric_weight_kg);
    setExtraFieldValues(src.extra_fields);

    const category = productCategories.find(
      (category) => category.id === src.product_category_id
    );

    const derivedProductTypeId = src.product_id ?? (category as ProductCategory | undefined)?.product_type_id;

    if (derivedProductTypeId) {
      setProductType(derivedProductTypeId);
      setValue('product_type_id', derivedProductTypeId, { shouldValidate: true, shouldDirty: true });
    }
    // Asegurar validar todo el formulario para habilitar Guardar
    setTimeout(() => {
      trigger();
    }, 0);
  }, [initialArticle, isCopy, article, productCategories, setValue, trigger, mode]);

  const filteredProductCategories = useMemo(() => {
    if (!productType) {
      return productCategories as ProductCategory[];
    }
    return productCategories.filter(
      (category) =>
        (category as ProductCategory).product_type_id === productType
    );
  }, [productCategories, productType]);

  useEffect(() => {
    let description = '';

    if (isPack) {
      description = 'PACK ';
    }

    if (name) {
      description = `${description}${name} `;
    }

    if (brand) {
      description = `${description}${brand} `;
    }

    if (format) {
      const frmt = formats.find((frmt) => frmt.id === format);
      description = `${description}${frmt?.name} `;
    }

         const type = productTypes.find((pType) => pType.id === productType);
     type?.extra_fields
       .filter((extraF) => extraF.in_description)
       .forEach((extraF) => {
         if (extraF.type === 'boolean') {
           // Para campos booleanos, solo agregar si están marcados
           if (extraFieldValues[extraF.name]) {
             description = `${description}${extraF.name} `;
           }
         } else {
           // Para otros tipos de campos, agregar si tienen valor
           const value = extraFieldValues[extraF.name];
           if (value) {
             description = `${description}${value} `;
           }
         }
       });

    setProductDescription(description);
  }, [
    brand,
    format,
    formats,
    name,
    extraFieldValues,
    productTypes,
    productType,
    isPack
  ]);

  function chunkArray(arr: any[], size = 3): ExtraField[][] {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  }

  const updateExtraFieldValue = useCallback(
    (field: ExtraField, value: any) => {
      let formattedValue: any = value;
      if (field.type === 'numeric') {
        formattedValue = value === '' || value === null ? undefined : +value;
      } else if (typeof value === 'string') {
        formattedValue = value.toUpperCase();
      }

      const newExtraFieldValues = {
        ...extraFieldValues,
        [field.name]: formattedValue,
      };
      setExtraFieldValues(newExtraFieldValues);
      setValue('extra_fields', newExtraFieldValues, { shouldDirty: true, shouldValidate: true });
    },
    [extraFieldValues, setValue]
  );

  const extraFields = useMemo(() => {
    if (productType) {
      const type = productTypes.find((pType) => pType.id === productType);

      return type ? chunkArray(type.extra_fields) : [];
    }

    return [];
  }, [productType, productTypes]);

  useEffect(() => {
    if (length && width && depth) {
      setVolumetricWeightKg((length * width * depth) / 4000);
    } else {
      setVolumetricWeightKg(0);
    }
  }, [length, width, depth]);

  const deletePhoto = useCallback(
    (index: number) => {
      const array = [...newFiles];

      array.splice(index, 1);
      setNewFiles(array);
    },
    [newFiles]
  );

  const getSuggestedPrice = useCallback(async () => {
    await getSuggestedPriceCore(
      productDescription,
      (price: number) => {
        if (typeof price === 'number' && !isNaN(price) && price > 0) {
          setValue('price', price, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });
        }
      },
      () => {
        console.log('Error al obtener precio sugerido');
      }
    );
  }, [productDescription, getSuggestedPriceCore, setValue]);

  const applySuggestedPrice = useCallback(() => {
    if (
      suggestedPrice &&
      typeof suggestedPrice === 'number' &&
      !isNaN(suggestedPrice) &&
      suggestedPrice > 0
    ) {
      setValue('price', suggestedPrice, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      clearSuggestion();
    }
  }, [suggestedPrice, setValue, clearSuggestion]);

  // Determinar si la categoría es obligatoria
  const isCategoryRequired = filteredProductCategories.length > 0;
  
  // Determinar cuántos campos son obligatorios
  const requiredFieldsCount = isCategoryRequired ? 4 : 3;

  const headerTitle = mode === 'edit' ? 'Editar Artículo' : 'Crear Artículo';

  return (
    <>
    <Page
      header={<AddHeader onBack={embedded ? onClose : undefined} title={headerTitle} />}
      content={
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-full flex-col p-32"
        >
          {/* Descripción sticky siempre visible */}
          <div className="sticky top-0 z-50 bg-white shadow-lg rounded-2xl p-24 mb-24 border border-gray-200" style={{zIndex: 100}}>
            <div className="flex justify-between items-start mb-16">
              <Typography variant="h6" className="font-semibold text-gray-800">
                Descripción del Producto
              </Typography>
              
              {/* Botones de acción fijos arriba a la derecha */}
              <div className="flex flex-col items-end gap-2">
                {errorCode === ERROR.ERR_409 && (
                  <Typography color="error" className="text-sm">
                    Ya existe un producto con ese código
                  </Typography>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outlined"
                    color="primary"
                    sx={{ borderRadius: 50 }}
                    disabled={loading}
                    onClick={resetForm}
                    size="large"
                    startIcon={<ClearIcon />}
                  >
                    Limpiar
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    sx={{ borderRadius: 50 }}
                    disabled={!isEmpty(errors) || !isValid || loading}
                    type="submit"
                    size="large"
                  >
                    <Spinner show={loading} />
                    Guardar
                  </Button>
                </div>
              </div>
            </div>
            <TextField
              variant="outlined"
              label="Descripción"
              disabled
              fullWidth
              value={productDescription}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                '& .MuiInputBase-input': {
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontWeight: 400,
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(0, 0, 0, 0.6)',
                }
              }}
            />
          </div>

          <Paper className="flex w-full flex-col shadow rounded-2xl mb-32 p-32 pt-40 mt-16">
            <div className="flex flex-col md:flex-row gap-24 mb-24">
              <div className="flex items-center gap-2 w-full">
                <Controller
                  name="code"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Código del Producto"
                      variant="outlined"
                      disabled={loading || withoutCode}
                      error={!!errors.code}
                      helperText={errors?.code?.message}
                      onChange={(ev) => {
                        field.onChange(ev);
                        setProductCode(ev.target.value);
                        debouncedChangeHandler(ev.target.value, 'code');
                      }}
                      fullWidth
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: withoutCode ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                        },
                        '& .MuiInputBase-input': {
                          fontWeight: withoutCode ? 400 : 600,
                          color: withoutCode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.87)',
                        }
                      }}
                    />
                  )}
                />
                {productCode && !withoutCode && (
                  <CopyButton
                    textToCopy={productCode}
                    tooltip="Copiar código del producto"
                  />
                )}
              </div>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={withoutCode}
                    onChange={(ev) => {
                      setWithoutCode(ev.target.checked);
                      if (ev.target.checked) {
                        setValue('code', '');
                        setProductCode('');
                      }
                    }}
                  />
                }
                className="!w-[300px]"
                label="Sin Código"
                sx={{ zIndex: 1 }}
              />

              <FormControl variant="outlined" className="!w-[200px]">
                <InputLabel id="product-prefix-label">Prefijo</InputLabel>
                <Select
                  labelId="product-prefix-label"
                  label="Prefijo"
                  value={prefix}
                  disabled={loading || !withoutCode}
                  onChange={(ev) => {
                    setPrefix(ev.target.value);
                  }}
                >
                  <MenuItem value="SC">SC</MenuItem>
                  <MenuItem value="GN">GN</MenuItem>
                </Select>
              </FormControl>
              <div className="flex items-center gap-2 w-full">
                <Controller
                  name="alternative_code"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Código alternativo"
                      variant="outlined"
                      disabled={loading || !withoutCode}
                      error={!!errors.alternative_code}
                      helperText={errors?.alternative_code?.message}
                      onChange={(ev) => {
                        field.onChange(ev);
                        setAlternativeCode(ev.target.value);
                        debouncedChangeHandler(
                          ev.target.value,
                          'alternative_code'
                        );
                      }}
                      fullWidth
                    />
                  )}
                />
                {alternativeCode && withoutCode && (
                  <CopyButton
                    textToCopy={alternativeCode}
                    tooltip="Copiar código alternativo"
                  />
                )}
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-24 mb-24">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPack}
                    onChange={(ev) => {
                      setIsPack(ev.target.checked);
                    }}
                  />
                }
                label="PACK"
                className="!mr-8"
                sx={{ zIndex: 1 }}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-24 mb-24">
              <Typography variant="body2" color="text.secondary" className="mb-16 col-span-full">
                Los campos marcados con * son obligatorios ({requiredFieldsCount} campos)
              </Typography>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Producto *"
                    variant="outlined"
                    disabled={loading}
                    error={!!errors.name}
                    helperText={errors?.name?.message}
                    onChange={(ev) => {
                      field.onChange(ev);
                      debouncedChangeHandler(ev.target.value, 'name');
                      setName(ev.target.value.toUpperCase());
                    }}
                    required
                    fullWidth
                  />
                )}
              />

              <Controller
                name="brand"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Marca *"
                    variant="outlined"
                    disabled={loading}
                    error={!!errors.brand}
                    helperText={errors?.brand?.message}
                    onChange={(ev) => {
                      field.onChange(ev);
                      debouncedChangeHandler(ev.target.value, 'brand');
                      setBrand(ev.target.value.toUpperCase());
                    }}
                    required
                    fullWidth
                  />
                )}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-24 mb-24">
              <FormControl variant="outlined" fullWidth required>
                <InputLabel id="product-type-label">
                  Tipo de Producto *
                </InputLabel>
                <Select
                  labelId="product-type-label"
                  label="Tipo de Producto *"
                  value={productType || ''}
                  onChange={(ev) => {
                    setExtraFieldValues({});
                    setProductType(+ev.target.value);
                    setValue('product_type_id', +ev.target.value);
                    // Limpiar categoría cuando cambie el tipo de producto
                    setValue('product_category_id', undefined);
                    // Forzar validación del formulario para recalcular el schema
                    setTimeout(() => {
                      trigger('product_category_id');
                    }, 0);
                  }}
                  disabled={loading}
                  required
                  error={!!errors.product_type_id && (formState.touchedFields.product_type_id || formState.isSubmitted)}
                >
                  {productTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {productType && isCategoryRequired && (
                <Controller
                  name="product_category_id"
                  control={control}
                  render={({ field }) => (
                    <CategorySelect
                      value={field.value}
                      onChange={field.onChange}
                      categories={filteredProductCategories as ProductCategory[]}
                      label="Categoría *"
                      required
                      error={!!errors.product_category_id}
                      helperText={errors?.product_category_id?.message}
                      disabled={loading}
                    />
                  )}
                />
              )}
            </div>

            {extraFields.map((subExtraFields, index) => (
              <div
                className="grid grid-cols-1 md:grid-cols-3 gap-24 mb-24"
                key={index}
              >
                {subExtraFields.map((fieldData, sIndex) =>
                  fieldData.ref === 'format' ? (
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel id="format-label">FORMATO</InputLabel>
                      <Select
                        labelId="format-label"
                        label="Formato"
                        disabled={loading}
                        value={extraFieldValues[fieldData.name]}
                        onChange={(ev) => {
                          setFormat(ev.target.value);
                          updateExtraFieldValue(fieldData, ev.target.value);
                          setValue('format', ev.target.value);
                        }}
                      >
                        {formats.map((format) => (
                          <MenuItem key={format.id} value={format.id}>
                            {format.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : fieldData.ref === 'genre' ? (
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel id="genre-label">GENERO</InputLabel>
                      <Select
                        labelId="genre-label"
                        label="Género"
                        disabled={loading}
                        value={extraFieldValues[fieldData.name]}
                        onChange={(ev) => {
                          updateExtraFieldValue(fieldData, ev.target.value);
                          setValue('genre', ev.target.value);
                        }}
                      >
                        {genres.map((genre) => (
                          <MenuItem key={genre.id} value={genre.id}>
                            {genre.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : fieldData.type === 'boolean' ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={extraFieldValues[fieldData.name] || false}
                          onChange={(ev) => {
                            updateExtraFieldValue(fieldData, ev.target.checked);
                          }}
                        />
                      }
                      label={fieldData.name}
                    />
                  ) : (
                    <TextField
                      key={sIndex}
                      label={fieldData.name}
                      type={
                        fieldData.type === 'alphanumeric' ? 'text' : 'number'
                      }
                      value={extraFieldValues[fieldData.name]}
                      onChange={(ev) =>
                        updateExtraFieldValue(fieldData, ev.target.value)
                      }
                      variant="outlined"
                      disabled={loading}
                      fullWidth
                    />
                  )
                )}
              </div>
            ))}
          </Paper>

          <Paper className="flex w-full flex-col shadow rounded-2xl p-32">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-24 mb-24">
              <Controller
                name="width_cm"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Ancho cm"
                    type="number"
                    variant="outlined"
                    disabled={loading}
                    onChange={(ev) => {
                      field.onChange(ev);
                      setWidth(+ev.target.value);
                    }}
                    error={!!errors.width_cm}
                    helperText={errors?.width_cm?.message}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="length_cm"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Largo cm"
                    type="number"
                    variant="outlined"
                    disabled={loading}
                    onChange={(ev) => {
                      field.onChange(ev);
                      setLength(+ev.target.value);
                    }}
                    error={!!errors.length_cm}
                    helperText={errors?.length_cm?.message}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="depth_cm"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Fondo cm"
                    type="number"
                    variant="outlined"
                    disabled={loading}
                    onChange={(ev) => {
                      field.onChange(ev);
                      setDepth(+ev.target.value);
                    }}
                    error={!!errors.depth_cm}
                    helperText={errors?.depth_cm?.message}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="weight_kg"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Peso kg"
                    type="number"
                    variant="outlined"
                    disabled={loading}
                    error={!!errors.weight_kg}
                    helperText={errors?.weight_kg?.message}
                    fullWidth
                  />
                )}
              />

              <TextField
                label="Peso Volumétrico kg"
                type="number"
                variant="outlined"
                disabled={true}
                value={volumetricWeightKg}
                fullWidth
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                  '& .MuiInputBase-input': {
                    color: 'rgba(0, 0, 0, 0.6)',
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(0, 0, 0, 0.6)',
                  }
                }}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-24 mb-24">
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Observaciones"
                    variant="outlined"
                    disabled={loading}
                    error={!!errors.notes}
                    helperText={errors?.notes?.message}
                    fullWidth
                  />
                )}
              />
            </div>
          </Paper>

          {/* Sección de Precio */}
          <Paper className="flex w-full flex-col shadow rounded-2xl mb-32 p-32 mt-16">
            <Typography variant="h6" className="mb-24 font-semibold">
              Precio de Mercado
            </Typography>

            <div className="flex flex-col gap-24">
              <Box className="flex flex-col gap-4">
                <Box className="flex items-center gap-4">
                  <Controller
                    name="price"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Precio de mercado"
                        type="number"
                        variant="outlined"
                        disabled={loading}
                        error={!!errors.price}
                        helperText={errors?.price?.message}
                        sx={{ minWidth: '200px', maxWidth: '300px' }}
                        InputLabelProps={{
                          shrink: !!field.value || field.value === 0,
                        }}
                      />
                    )}
                  />
                </Box>

                <Box className="flex items-center gap-3">
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={getSuggestedPrice}
                    disabled={
                      loading || loadingSuggestion || !productDescription.trim()
                    }
                    sx={{
                      whiteSpace: 'nowrap',
                      minWidth: '180px',
                      px: 3,
                      py: 1.5,
                    }}
                  >
                    {loadingSuggestion ? (
                      <CircularProgress size={16} />
                    ) : (
                      'Obtener Precio Sugerido'
                    )}
                  </Button>

                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      const searchQuery = encodeURIComponent(productDescription.trim());
                      window.open(`https://www.google.com/search?q=${searchQuery}`, '_blank');
                    }}
                    disabled={!productDescription.trim()}
                    sx={{
                      whiteSpace: 'nowrap',
                      minWidth: '140px',
                      px: 3,
                      py: 1.5,
                    }}
                  >
                    Buscar en Web
                  </Button>
                </Box>

                {suggestedPrice &&
                  typeof suggestedPrice === 'number' &&
                  !isNaN(suggestedPrice) &&
                  suggestedPrice > 0 && (
                    <Box className="flex items-center gap-2 mt-2">
                      <Chip
                        label={`Precio aplicado: ${formatPrice(
                          suggestedPrice
                        )}`}
                        color="success"
                        variant="outlined"
                        size="medium"
                        onClick={applySuggestedPrice}
                        onDelete={clearSuggestion}
                        clickable
                        sx={{ fontSize: '14px', fontWeight: 'medium' }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Se aplicó automáticamente. Haz clic para aplicar
                        nuevamente o usa X para limpiar.
                      </Typography>
                    </Box>
                  )}

                {suggestionError && (
                  <Typography variant="caption" color="error" className="mt-1">
                    {suggestionError}
                  </Typography>
                )}
              </Box>
            </div>
          </Paper>

          {/* Sección de Fotos */}
          <Paper className="flex w-full flex-col shadow rounded-2xl mb-32 p-32 mt-16">
            <Typography variant="h6" className="mb-24 font-semibold">
              Fotos del Producto
            </Typography>

            <div className="mb-24 flex flex-col items-center">
              <label htmlFor="inputFiles">
                <Button
                  color="primary"
                  aria-label="upload picture"
                  component="span"
                  className="mb-32"
                >
                  <PhotoCamera />
                  Seleccionar Fotos
                </Button>
              </label>
              <input
                id="inputFiles"
                type="file"
                accept="image/*"
                className="hidden"
                multiple
                onChange={(e) => {
                  if (e.target.files) {
                    const files = Array.from(e.target.files);
                    setNewFiles(files);
                  } else {
                    setNewFiles([]);
                  }
                }}
              />
              <div className="flex flex-wrap justify-center gap-12 mt-4">
                {newFiles.map((file, index) => (
                  <div
                    key={index}
                    className="w-[100px] h-[100px] rounded shadow relative"
                  >
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Preview ${index}`}
                      className="object-cover w-[100px] h-[100px]"
                    />
                    <IconButton
                      color="error"
                      aria-label="delete picture"
                      component="span"
                      className="absolute top-0 right-0"
                      onClick={() => deletePhoto(index)}
                    >
                      <HighlightOffIcon />
                    </IconButton>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-center gap-8">
              {errorCode === ERROR.ERR_409 && (
                <Typography color="error" className="text-md">
                  Ya existe un producto con ese código
                </Typography>
              )}
            </div>
          </Paper>
        </form>
      }
      scroll={isMobile ? 'normal' : 'content'}
    />
    
    {showSuccessModal && (
      <Dialog
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            minHeight: 400,
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'success.main' }}>
                ✓
              </Avatar>
              <Typography variant="h5" color="success.main" fontWeight="bold">
                {mode === 'edit' ? '¡Producto Editado Exitosamente!' : '¡Producto Creado Exitosamente!'}
              </Typography>
            </Box>
            <IconButton 
              onClick={() => setShowSuccessModal(false)}
              size="small"
              color="error"
            >
              ✕
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {createdArticle && (
            <Box display="flex" flexDirection="column" gap={3}>
              {/* Información principal del producto */}
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Grid container spacing={3}>
                  {/* Imagen del producto */}
                  <Grid item xs={12} md={4}>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                      {createdArticle.files && createdArticle.files.length > 0 ? (
                        <img
                          src={createdArticle.files[0].URL}
                          alt={createdArticle.name}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '200px',
                            objectFit: 'contain',
                            borderRadius: '8px'
                          }}
                        />
                      ) : (
                        <Avatar
                          sx={{
                            width: 120,
                            height: 120,
                            bgcolor: 'grey.300',
                            fontSize: '3rem'
                          }}
                        >
                          📦
                        </Avatar>
                      )}
                    </Box>
                  </Grid>

                  {/* Detalles del producto */}
                  <Grid item xs={12} md={8}>
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {createdArticle.name}
                      </Typography>
                      
                      <Typography variant="body1" color="text.secondary">
                        <strong>Marca:</strong> {createdArticle.brand}
                      </Typography>
                      
                      {createdArticle.description && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Descripción:</strong> {createdArticle.description}
                        </Typography>
                      )}
                      
                      {createdArticle.price > 0 && (
                        <Typography variant="h6" color="success.main" fontWeight="bold">
                          ${createdArticle.price.toLocaleString('es-ES')}
                        </Typography>
                      )}
                      
                      <Box display="flex" gap={1} flexWrap="wrap">
                        {createdArticle.pack && (
                          <Chip label="PACK" color="primary" size="small" />
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Código de barras */}
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Código de Barras
                </Typography>
                
                <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="body2" color="text.secondary">
                      Código: {createdArticle.code}
                    </Typography>
                    <CopyButton
                      textToCopy={createdArticle.code || ''}
                      tooltip="Copiar código"
                    />
                  </Box>
                  
                  {createdArticle.code && (
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: 'white', 
                      borderRadius: 1, 
                      border: '1px solid #e0e0e0' 
                    }}>
                      <ReactBarcode
                        value={createdArticle.code}
                        fontSize={14}
                        height={60}
                        format="CODE128"
                        margin={0}
                        displayValue={true}
                        background="#FFFFFF"
                        textAlign="center"
                      />
                    </Box>
                  )}
                  
                  {/* Botón de imprimir etiqueta */}
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<PrintIcon />}
                    onClick={() => {
                      // Usar la función específica para imprimir artículos
                      printArticle(createdArticle);
                    }}
                    sx={{ mt: 2 }}
                  >
                    Imprimir Etiqueta
                  </Button>
                </Box>
              </Paper>

              {/* Información adicional */}
              { createdArticle && (createdArticle.width_cm > 0 || createdArticle.length_cm > 0 || createdArticle.depth_cm > 0 || createdArticle.weight_kg > 0) && <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Información Adicional
                </Typography>
                
                <Grid container spacing={2}>
                  {createdArticle.width_cm > 0 && (
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Ancho:</strong> {createdArticle.width_cm} cm
                      </Typography>
                    </Grid>
                  )}
                  {createdArticle.length_cm > 0 && (
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Largo:</strong> {createdArticle.length_cm} cm
                      </Typography>
                    </Grid>
                  )}
                  {createdArticle.depth_cm > 0 && (
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Fondo:</strong> {createdArticle.depth_cm} cm
                      </Typography>
                    </Grid>
                  )}
                  {createdArticle.weight_kg > 0 && (
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Peso:</strong> {createdArticle.weight_kg} kg
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setShowSuccessModal(false)}
            size="large"
          >
            Cerrar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              setShowSuccessModal(false);
              goToArticles();
            }}
            size="large"
          >
            Ir al Listado
          </Button>
        </DialogActions>
      </Dialog>
    )}
  </>
);
};

export default CreateArticle;
